/**
 * Gepufferte Änderungen: Was ohne Verbindung geändert wird, landet hier und geht
 * in derselben Reihenfolge an den Server, sobald er wieder erreichbar ist.
 * Jede Änderung trägt einen Idempotenz-Schlüssel – doppelt gesendet wirkt sie nur einmal.
 */
import { IDEMPOTENCY_HEADER, type NoteDetail } from '@kartothek/shared';
import { ApiError } from '../apiError';
import { t } from '../i18n';
import { looksLikeCombat, looksLikeMap, looksLikeNote, noteUrl, readCached, storeWriteResponse } from './cache';
import { offlineHandler } from './handlers';
import { idbCount, idbDelete, idbGetAll, idbPut } from './idb';
import { getOfflineState, isUnreachableStatus, reportOffline, reportOnline, setOfflineState } from './state';

export interface OutboxOp {
  seq?: number;
  key: string;
  method: string;
  url: string;
  body: unknown;
  cid: string | null;
  at: string;
  /** Kurzbeschreibung für Hinweise („Notiz „Vrek““). */
  label: string;
  attempts: number;
}

export interface SyncProblem {
  id?: number;
  kind: 'conflict' | 'rejected';
  cid: string | null;
  at: string;
  label: string;
  message: string;
  /** Bei Konflikten: die lokale Fassung der Notiz, damit nichts verloren geht. */
  noteId?: string;
  local?: { body?: string; frontmatter?: Record<string, unknown> };
}

const NOTE_PUT = /^\/api\/campaigns\/([0-9a-f-]{36})\/notes\/([0-9a-f-]{36})$/i;
const CAMPAIGN = /^\/api\/campaigns\/([0-9a-f-]{36})/i;

export function newKey(): string {
  // randomUUID gibt es nur in sicheren Kontexten (HTTPS/localhost)
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

export async function refreshCounts(): Promise<void> {
  setOfflineState({ pending: await idbCount('outbox'), problems: await idbCount('problems') });
}

function describe(result: unknown): string {
  if (looksLikeNote(result)) return t('offline.labelNote', { title: result.title });
  if (looksLikeCombat(result)) return t('offline.labelCombat', { name: result.name });
  const action = result as { combat?: unknown } | null;
  if (action && looksLikeCombat(action.combat)) return t('offline.labelCombat', { name: action.combat.name });
  if (looksLikeMap(result)) return t('offline.labelMap');
  return t('offline.labelOther');
}

/** Lässt sich diese Änderung offline ausführen? */
export function canQueue(method: string, url: string): boolean {
  return offlineHandler(method, url) !== null;
}

/**
 * Änderung lokal ausführen und für den Server puffern. Wirft, wenn sie offline nicht geht
 * oder gegen Regeln verstößt (dann wird nichts gepuffert).
 */
export async function enqueue(method: string, url: string, body: unknown, key = newKey()): Promise<unknown> {
  const run = offlineHandler(method, url);
  if (!run) throw new ApiError(0, 'offline', t('offline.unsupported'));
  const result = await run(body);
  const op: OutboxOp = {
    key,
    method,
    url,
    body,
    cid: CAMPAIGN.exec(url)?.[1]?.toLowerCase() ?? null,
    at: new Date().toISOString(),
    label: describe(result),
    attempts: 0,
  };
  await idbPut('outbox', op);
  await refreshCounts();
  // Vielleicht ist der Server längst wieder da
  if (getOfflineState().online) void flushOutbox();
  return result;
}

async function addProblem(p: SyncProblem) {
  await idbPut('problems', p);
}

export async function listProblems(): Promise<SyncProblem[]> {
  return idbGetAll<SyncProblem>('problems');
}

export async function dismissProblem(id: number): Promise<void> {
  await idbDelete('problems', id);
  await refreshCounts();
}

type FlushListener = (campaignIds: string[]) => void;
const flushListeners = new Set<FlushListener>();
/** Nach dem Senden: betroffene Kampagnen neu abgleichen. */
export function onFlushed(fn: FlushListener): () => void {
  flushListeners.add(fn);
  return () => flushListeners.delete(fn);
}

let flushing: Promise<void> | null = null;

export function flushOutbox(): Promise<void> {
  if (flushing) return flushing;
  const run = async () => {
    // Mehrere Tabs: nur einer sendet
    if (navigator.locks) await navigator.locks.request('kartothek-outbox', () => flushOnce());
    else await flushOnce();
  };
  flushing = run().finally(() => {
    flushing = null;
  });
  return flushing;
}

async function flushOnce(): Promise<void> {
  let ops = (await idbGetAll<OutboxOp>('outbox')).sort((a, b) => a.seq! - b.seq!);
  if (ops.length === 0) return;
  setOfflineState({ syncing: true });
  const touched = new Set<string>();
  // Stand jeder Notiz nach bereits gesendeten Änderungen (für die optimistische Sperre)
  const serverStand = new Map<string, string>();
  try {
    while (ops.length > 0) {
      const op = ops[0]!;
      let body = op.body as Record<string, unknown> | undefined;
      const note = op.method === 'PUT' ? NOTE_PUT.exec(op.url) : null;
      if (note && body?.baseUpdatedAt && serverStand.has(note[2]!)) body = { ...body, baseUpdatedAt: serverStand.get(note[2]!) };

      let res: Response;
      try {
        res = await fetch(op.url, {
          method: op.method,
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'kartothek', 'Content-Type': 'application/json', [IDEMPOTENCY_HEADER]: op.key },
          body: JSON.stringify(body ?? {}),
        });
      } catch {
        reportOffline();
        return;
      }
      if (isUnreachableStatus(res.status)) {
        reportOffline();
        return;
      }
      reportOnline();
      // Abgemeldet: nach dem Anmelden geht es weiter
      if (res.status === 401) return;
      let data: unknown = null;
      try {
        data = await res.json();
      } catch {
        /* keine JSON-Antwort */
      }
      if (res.status >= 500) {
        op.attempts++;
        if (op.attempts < 3) {
          await idbPut('outbox', op);
          return;
        }
        await addProblem({ kind: 'rejected', cid: op.cid, at: op.at, label: op.label, message: t('offline.serverError') });
      } else if (res.ok) {
        await storeWriteResponse(op.url, data);
        if (note && looksLikeNote(data)) serverStand.set(data.id, data.updatedAt);
      } else if (res.status === 409 && (data as { error?: string })?.error === 'stale' && note) {
        // Konflikt: jemand anderes hat die Notiz geändert. Die letzte lokale Fassung als Problem aufheben.
        const later = ops.slice(1).filter((o) => o.method === 'PUT' && o.url === op.url);
        const last = ((later.at(-1) ?? op).body ?? {}) as { body?: string; frontmatter?: Record<string, unknown> };
        for (const o of later) await idbDelete('outbox', o.seq!);
        ops = ops.filter((o) => !later.includes(o));
        await addProblem({
          kind: 'conflict',
          cid: op.cid,
          at: op.at,
          label: op.label,
          message: t('offline.conflict'),
          noteId: note[2]!.toLowerCase(),
          local: { body: last.body, frontmatter: last.frontmatter },
        });
      } else {
        const msg = (data as { message?: string })?.message ?? res.statusText;
        await addProblem({ kind: 'rejected', cid: op.cid, at: op.at, label: op.label, message: msg });
      }
      await idbDelete('outbox', op.seq!);
      if (op.cid) touched.add(op.cid);
      ops = ops.slice(1);
      await refreshCounts();
    }
  } finally {
    setOfflineState({ syncing: false });
    await refreshCounts();
    if (touched.size > 0) for (const fn of flushListeners) fn([...touched]);
  }
}

/** Titel einer Notiz aus der lokalen Kopie (für den Konfliktdialog). */
export async function cachedNote(cid: string, id: string): Promise<NoteDetail | undefined> {
  return readCached<NoteDetail>(noteUrl(cid, id));
}
